<template>
  <div class="courses-list" :class="$config.project">
    <div class="hero-section">
      <div class="holder-hero-section container">
        <div class="row">
          <div class="holder-title col col-7">
            <h1 class="h3">
              Courses Library
            </h1>
            <p>
              Discover a content-rich data science curriculum designed to build your skillset from beginner to job-ready and help you achieve your data science career goals.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div v-if="$config.project === 'lms'" class="container">
      <div class="filters">
        <form
          action=""
          method="post"
          @submit.prevent=""
        >
          <div class="search-bar-holder">
            <div class="search-bar">
              <input
                v-model="keyword"
                type="text"
                name="search"
                autocomplete="off"
                placeholder="Search courses"
              >
              <SearchBarIcon class="search-icon" />
              <CloseIcon v-if="keyword" class="close-btn" @click="clearInput" />
            </div>
          </div>
        </form>
        <Filters
          slot="filters"
          :filters="filters"
          @change="filtrate"
        />
      </div>
    </div>
    <div class="container course-library" :class="{ 'results': $config.project === 'lms' && $store.state.layout === 'mobile' && (!courses || !courses.length) }">
      <h1 v-if="$config.project === 'site'" class="page-title">
        Data Science Courses Library
      </h1>
      <div class="row">
        <aside class="col col-3 md-4">
          <div class="menus-wrapper">
            <div v-if="$config.project === 'lms' && (inProgress.length || completed.length || recommendedCourses.length)" class="menu">
              <p class="h5">
                My Courses
              </p>
              <ul>
                <li
                  v-if="inProgress.length" data-section="in-progress" :class="{ active: activeSection === 'in-progress' }"
                  @click="scrollToSection"
                >
                  In Progress ({{ inProgress.length }})
                </li>
                <li
                  v-if="recommendedCourses.length" data-section="recommended" :class="{ active: activeSection === 'recommended' }"
                  @click="scrollToSection"
                >
                  Recommended ({{ recommendedCourses.length }})
                </li>
                <li
                  v-if="completed.length" data-section="completed" :class="{ active: activeSection === 'completed' }"
                  @click="scrollToSection"
                >
                  Completed ({{ completed.length }})
                </li>
              </ul>
            </div>
            <div v-if="Object.keys(modules).length" class="menu">
              <p v-if="$config.project === 'lms'" class="h5">
                Course Library
              </p>
              <p v-else class="h6">
                {{ Object.values(modules).length }} Course Modules
              </p>
              <ul>
                <template v-for="(module, i) in modules">
                  <li
                    v-if="modulesCourses[module.id]"
                    :key="module.id"
                    :data-section="`module${module.id}`"
                    :class="{ active: activeSection === `module${module.id}`, passed: activeSectionIdx > module.id, first: +i === 1, last: +i === Object.values(modules).length }"
                    @click="scrollToSection"
                  >
                    <span>{{ i }}</span> {{ module.name }}
                  </li>
                </template>
                <li v-if="$config.project === 'site'" class="active-background">
                  &nbsp;
                </li>
              </ul>
            </div>
          </div>
        </aside>
        <div class="col col-9 md-8">
          <h5 v-if="$config.project === 'lms' && (inProgress.length || completed.length || recommendedCourses.length)" class="mobile-section-title">
            My Courses
          </h5>
          <template v-if="$store.state.auth.user">
            <div v-if="inProgress.length" id="in-progress" class="module-section sliding">
              <Slider :key="inProgress.length" class-name="courses" :disabled="$store.state.layout === 'desktop' && inProgress.length <= 3">
                <h4 slot="heading" class="module-title in-progress" @click="expandCourses">
                  <div>
                    In Progress
                    <span class="courses-count">{{ inProgress.length }} courses</span>
                  </div>
                  <Arrow v-if="$config.project === 'lms'" />
                  <Arrow2 v-else class="arrow-site" />
                </h4>
                <Card
                  v-for="course in inProgress"
                  :key="course.id"
                  slot="slide"
                  :course="course"
                  class="col col-4 md-6 sm-6"
                />
              </Slider>
            </div>
            <div v-if="recommendedCourses.length" id="recommended" class="module-section sliding">
              <Slider :key="recommendedCourses.length" class-name="courses" :disabled="$store.state.layout === 'desktop' && recommendedCourses.length <= 3">
                <h4 slot="heading" class="module-title recommended" @click="expandCourses">
                  <div>
                    My recommendations
                    <span class="courses-count">{{ recommendedCourses.length }} courses</span>
                  </div>
                  <Arrow v-if="$config.project === 'lms'" />
                  <Arrow2 v-else class="arrow-site" />
                </h4>
                <Card
                  v-for="course in recommendedCourses"
                  :key="course.id"
                  slot="slide"
                  :course="course"
                  class="col col-4 md-6 sm-6"
                />
              </Slider>
            </div>
            <div v-if="completed.length" id="completed" class="module-section sliding">
              <Slider :key="completed.length" class-name="courses" :disabled="$store.state.layout === 'desktop' && completed.length <= 3">
                <h4 slot="heading" class="module-title completed" @click="expandCourses">
                  <div>
                    Completed
                    <span class="courses-count">{{ completed.length }} courses</span>
                  </div>
                  <Arrow v-if="$config.project === 'lms'" />
                  <Arrow2 v-else class="arrow-site" />
                </h4>
                <Card
                  v-for="course in completed"
                  :key="course.id"
                  slot="slide"
                  :course="course"
                  class="col col-4 md-6 sm-6"
                />
              </Slider>
            </div>
          </template>
          <h5 class="mobile-section-title" :class="[[$config.project], { 'results': $config.project === 'lms' && (!courses || !courses.length) }]">
            {{ $config.project === 'lms' ? 'Course Library' : '5 Course Modules' }}
          </h5>
          <span v-if="$config.project === 'lms' && (!courses || !courses.length)" class="results">0 results</span>
          <div
            v-for="(modCourses, idx) in modulesCourses"
            :id="`module${idx}`"
            :key="idx"
            class="module-section tracking-section"
            :data-event="`module_${sections[idx]}.scroll`"
          >
            <div class="h4 module-title" @click="expandCourses">
              <div>
                Module {{ idx }} | <h2 class="heading-reset">
                  {{ modules[idx].name }}
                </h2>
                <span class="courses-count">{{ modCourses.length }} courses</span>
              </div>
              <Arrow v-if="$config.project === 'lms'" />
              <Arrow2 v-else class="arrow-site" />
            </div>
            <component :is="$config.project === 'site' && (idx > 1 || $store.state.layout === 'mobile') ? 'Lazy' : 'div'" v-bind="idx > 1 ? { never: true } : {}">
              <div class="row">
                <div class="module-description">
                  <p>{{ modules[idx].description }}</p>
                </div>
                <Card
                  v-for="modCourse in modCourses"
                  :key="modCourse.id"
                  :course="modCourse"
                  class="col col-4 md-6 sm-6"
                  heading="h3"
                />
                <div v-if="idx <= 4 && $config.project === 'site'" class="course-card col col-4 md-6 sm-6">
                  <div class="course-banner" :class="{ light: idx % 2 === 0 }">
                    <p class="h3">
                      Learn Data Science with Industry Experts
                    </p>
                    <nuxt-link to="/signup/" class="btn btn-primary" data-event="start_your_career_2.click">
                      Start Your Career
                    </nuxt-link>
                  </div>
                </div>
                <div v-else-if="$config.project === 'lms' && (idx === '1' || idx === '4') && !$store.getters['banners/promoBanner'] && !$store.getters['banners/triggerBanner']" class="course-card col col-4 md-6 sm-6">
                  <Banner
                    v-if="$store.getters['banners/alwaysBanner'] || $store.getters['banners/triggerBanner']" :banner-type="[0, 1]" side
                    class="courses"
                  >
                    <CourseBannerIcon slot="icon" />
                  </Banner>
                </div>
              </div>
            </component>
          </div>
          <slot name="banner" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { groupBy } from 'lodash-es';
import { delayOnKeyup } from '../utils/helper';
import Arrow from '../assets/svg/curved-arrow.svg?vue-component';
import Arrow2 from '../assets/svg/icon-arrow-right.svg?vue-component';
import CloseIcon from '../assets/svg/close-icon.svg?vue-component';
import CourseBannerIcon from '../assets/svg/icon-course-banner.svg?vue-component';
import SearchBarIcon from '../assets/svg/icon-search.svg?vue-component';
import Card from './Card';

import Slider from '../components/Slider';
import Filters from '../components/Filters';
import SliderHeader from './Slider';
import Banner from '../Banner';
import global from '../mixins/global';

/* eslint-disable no-restricted-syntax */
/* eslint-disable no-prototype-builtins */

import { TECHNOLOGIES } from '../utils/constants';

export default {
  components: {
    Card, SliderHeader, Slider, Filters, Banner, Arrow, Arrow2, CloseIcon, CourseBannerIcon, SearchBarIcon, Lazy: async () => import('vue-lazy-hydration'),
  },
  mixins: [global],
  props: {
    list: { type: Object, required: false, default: () => ({ }) },
  },
  data() {
    return {
      sections: {
        1: 'one',
        2: 'two',
        3: 'three',
        4: 'four',
      },
      modules: [],
      courses: this.list.courses || [],
      coursesPopular: [],
      coursesList: [],
      recommendedCourses: this.recommendations ? this.list.recommendations.courses : [],
      page: {},
      activeSection: 'module1',
      clickedSection: null,
      keyword: '',
      filter: null,
    };
  },
  // eslint-disable-next-line consistent-return
  async fetch() {
    try {
      let {
        // eslint-disable-next-line prefer-const
        modules, courses, page, recommendations, popular,
      } = this.list;

      if (!this.list.courses) {
        ({
          modules, courses, page, recommendations,
        } = await this.$axios.$get('/courses'));
      }

      page.fullPath = `${this.$config.siteUrl}${this.$route.fullPath}`;
      page.metaFullImageUrl = `${this.$config.siteUrl}/resources/${page.metaImage}`;

      page.metaImageDimensions = page.metaImageDimensions || {};
      page.created = this.dateIsoString(page.created || new Date());
      page.updated = this.dateIsoString(page.updated || new Date());

      courses.forEach((item, i) => {
        this.coursesList.push({
          '@type': 'ListItem',
          position: i + 1,
          url: `${this.$config.siteUrl}/courses/${item.slug}/`,
        });
      });

      this.modules = modules.reduce((p, c) => ({ ...p, [c.id]: c }), {});
      this.courses = courses;
      this.recommendedCourses = recommendations ? recommendations.courses : [];
      this.coursesPopular = popular;
      if (this.inProgress.length) {
        this.activeSection = 'in-progress';
      } else if (this.completed.length) {
        this.activeSection = 'completed';
      }

      this.page = page;
    } catch (error) {
      console.log(error);
      return this.$router.push('/');
    }
  },
  head() {
    if (this.$config.project === 'site') {
      const linkedData = this.$linkedDataLoad([
        { key: 'WebSite' },
        { key: 'WebPage', values: { ...this.page } },
        {
          key: 'CoursesList',
          values: {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: this.coursesList || [],
          },
        },
      ]);

      const link = [];

      if (this.canonicalURL) {
        link.push({ rel: 'canonical', href: this.canonicalURL() });
      }

      return {
        titleTemplate: `${this.page.metaTitle}%s`,
        link,
        meta: this.metaProps(this.page),
        script: [linkedData],
      };
    }

    return { titleTemplate: `${this.page.metaTitle}%s`, meta: this.metaProps(this.page) };
  },
  computed: {
    activeSectionIdx() {
      return +this.activeSection.replace('module', '') || 0;
    },
    popular() {
      const popular = this.courses.filter((c) => c.popular).slice(0, 3);
      const [progress] = this.inProgress;
      if (progress) {
        const lastPopular = popular.find((p) => p.id === progress.id);
        if (!lastPopular) {
          popular[0] = progress;

          return popular;
        }

        return [
          progress,
          ...popular.filter((p) => p.id !== progress.id),
        ];
      }

      return popular;
    },
    modulesCourses() {
      return groupBy(this.courses, (course) => course.moduleId);
    },
    inProgress() {
      return this.courses.filter((c) => (c.progress > 0 || c.started) && !c.completed).sort((a, b) => new Date(b.latestCompleted) - new Date(a.latestCompleted));
    },
    completed() {
      return this.courses.filter((c) => c.completed).sort().sort((a, b) => new Date(b.completed) - new Date(a.completed));
    },
    filters() {
      return {
        technology: {
          name: 'Course technology',
          type: 'multiple',
          values: TECHNOLOGIES[this.$config.platform],
        },
      };
    },
  },
  watch: {
    keyword() {
      delayOnKeyup(async () => {
        this.search();
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('scroll', this.handleScroll);
      this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', this.handleScroll));
    });
  },
  methods: {
    handleScroll() {
      if (this.$store.state.layout === 'mobile' && this.clickedSection) {
        const yOffset = this.$config.project === 'lms' ? -64 : -68;
        const element = document.querySelector(`#${this.clickedSection}`);
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        if (y < window.pageYOffset) {
          window.scrollTo({ top: y, behavior: 'instant' });
        }
      }
      const sections = document.querySelectorAll('.module-section');
      // 90 + 2 - fix firefox not showing current right
      const current = Array.from(sections).filter((s) => s.getBoundingClientRect().top - 92 <= 0);
      const element = current[current.length - 1];
      const menu = document.querySelector('.menus-wrapper .menu ul');

      if (menu) {
        const activeElement = document.querySelector('.menus-wrapper .menu ul li.active-background');

        if (element) {
          this.activeSection = element.id;
          if (activeElement) {
            const currentElement = document.querySelectorAll(`.menus-wrapper .menu ul li[data-section="${element.id}"]`)[0].getBoundingClientRect();
            activeElement.style.top = `${currentElement.top - menu.getBoundingClientRect().top}px`;
          }
        }
      }
    },
    async scrollToSection(e) {
      let yOffset;
      if (this.$store.state.layout === 'desktop') {
        yOffset = -90;
      } else {
        yOffset = this.$config.project === 'lms' ? -64 : -68;
      }
      const section = `${e?.target?.getAttribute('data-section') || e}`;
      const element = document.querySelector(`#${section}`);
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      setTimeout(() => {
        this.clickedSection = section;
        window.scrollTo({ top: y, behavior: 'smooth' });

        setTimeout(() => {
          this.clickedSection = null;
        }, 500);
      }, 200);
    },

    expandCourses(e) {
      if (this.$store.state.layout === 'desktop') {
        return;
      }

      const module = e.currentTarget.closest('.module-section').id;
      let currentSection = e.currentTarget.nextElementSibling;

      if (e.currentTarget.classList.contains('completed')) {
        currentSection = document.getElementById('completed').querySelector('.slider-container .row');
      } else if (e.currentTarget.classList.contains('in-progress')) {
        currentSection = document.getElementById('in-progress').querySelector('.slider-container .row');
      } else if (e.currentTarget.classList.contains('recommended')) {
        currentSection = document.getElementById('recommended').querySelector('.slider-container .row');
      }

      if (!currentSection.classList.contains('row')) {
        currentSection = currentSection.querySelector('.row');
      }
      const expanded = currentSection.classList.contains('expanded');

      const sections = this.$config.project === 'site' ? document.querySelectorAll('.module-title + .row') : document.querySelectorAll('.module-title + div .row');
      sections.forEach((s) => {
        const el = s;
        el.style.height = '0px';
        el.classList.remove('expanded');
      });

      if (this.$config.project === 'lms') {
        document.querySelectorAll('.slider-container .row').forEach((s) => {
          const el = s;
          el.style.height = '0px';
          el.classList.remove('expanded');
        });
      }

      const titles = document.querySelectorAll('.module-title');
      titles.forEach((t) => {
        t.classList.remove('expanded');
      });

      if (!expanded) {
        e.currentTarget.classList.add('expanded');
        currentSection.classList.add('expanded');
        currentSection.style.height = `${currentSection.scrollHeight + 20}px`;
      } else {
        currentSection.classList.add('expanded');
        setTimeout(() => {
          currentSection.classList.remove('expanded');
        }, 500);
      }

      this.scrollToSection(module);
    },

    async search() {
      const { courses, recommendations } = await this.$axios.$get(`/courses?q=${this.keyword}&${this.filter ? this.filter : ''}`);
      this.courses = courses;
      this.recommendedCourses = recommendations ? recommendations.courses : [];
    },
    filtrate(f) {
      this.filter = f.params;
      this.search();
    },
    clearInput() {
      this.keyword = '';
    },
  },
};
</script>

<style lang="scss">
.courses-list {
  position: relative;
  margin-top: 0;

  .hero-section {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2), rgba(86, 191, 199, 0) 100%), #000C1F;
    min-height: 212px;
    display: flex;
    align-items: center;

    .holder-title {
      color: var(--main-light);

      p {
        color: var(--main-light-shade-4);
        margin-bottom: 0;
      }
    }
  }

  &.site {
    padding-top: 0;

    .hero-section {
      padding-top: var(--spacing-80);
      min-height: 348px;

      .holder-title {
        .h3 {
          font-size: 40px;
          line-height: 110%;
        }

        p {
          line-height: 150%;
        }
      }
    }
  }

  aside {
    .menus-wrapper {
      position: sticky;
      position: -webkit-sticky;
      top: 100px;
    }

    .h6 {
      padding-bottom: var(--spacing-24);
      line-height: 20px;
    }

    .h5 {
      margin: 0;
      padding-bottom: var(--spacing-16);
      font-weight: 600;
    }

    .h6,
    .h5 {
      margin: 0;
      border-bottom: 1px solid var(--main-light-shade-3);
    }

    .menu {
      margin-bottom: var(--spacing-32);

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          padding: var(--spacing-12) 0 var(--spacing-12) 2px;
          line-height: 24px;
          cursor: pointer;
          display: flex;
          align-items: center;
          overflow: hidden;

          &.active span {
            background-color: var(--accent);
          }

          &.active,
          &:hover {
            color: var(--accent);
            text-shadow: 0 0 1px var(--accent);
          }

          span {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            display: inline-block;
            background-color: var(--main-light-shade-4);
            color: var(--main-light);
            text-align: center;
            line-height: 28px;
            flex: 0 0 auto;
            margin-right: var(--spacing-8);
            position: relative;
            font-weight: 600;

            &:before,
            &:after {
              content: "";
              position: absolute;
              width: 2px;
              height: 100%;
              left: 50%;
              background: var(--main-light-shade-4);
              transform: translateX(-50%);
            }

            &:before {
              bottom: 100%;
            }

            &:after {
              top: 100%;
            }
          }

          &.first span:before,
          &.last span:after {
            content: unset;
          }

          &.passed span:before,
          &.passed span:after,
          &.active span:before,
          &.passed span {
            background-color: var(--main-light-shade-4);
          }
        }
      }
    }
  }

  .filters {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--spacing-24);
    width: 100%;

    form {
      width: 100%;
      margin-right: var(--spacing-16);

      .search-bar {
        position: relative;

        input {
          border: 1px solid #F0F0F2;
          border-radius: var(--spacing-100);
          padding: var(--spacing-8) var(--spacing-44);
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 10px;
        }

        .close-btn {
          position: absolute;
          right: 13px;
          top: 14px;
          cursor: pointer;

          path[fill] {
            fill: var(--main-light-shade-3);
          }
        }
      }
    }
  }

  .course-library {
    .page-title {
      font-size: 28px;
      font-weight: 500;
      line-height: 26px;
      margin-bottom: var(--spacing-60);
    }

    .title {
      padding-top: var(--spacing-md);
      margin: 0;
      font-size: var(--fs-large);
    }

    .mobile-section-title {
      display: none;
    }

    .row {
      justify-items: stretch;
    }

    .module-section {
      padding: 0 0 var(--spacing-60);
      margin: 0 0 var(--spacing-60);

      &:not(:last-child) {
        border-bottom: 1px solid var(--main-light-shade-3);
      }

      &.sliding {
        padding: 0 0 var(--spacing-30);
      }
    }

    .module-title {
      font-weight: 400;
      margin: 0;
      margin-bottom: var(--spacing-16);

      &.completed,
      &.in-progress,
      &.recommended {
        margin-bottom: 0;
        font-weight: 400;

        .courses-count {
          display: none;
        }
      }

      .courses-count {
        float: right;
        font-size: 16px;
        color: var(--main-dark-shade-3);
        line-height: 38px;
        font-weight: normal;
      }

      .heading-reset {
        font-size: inherit;
        display: inline-block;
        font-weight: inherit;
        margin: 0;
        line-height: 1.3;
      }

      svg {
        display: none;
      }
    }

    .module-description {
      line-height: 24px;
      color: var(--main-dark-light-4);
      margin-left: var(--spacing-20);
      margin-right: var(--spacing-10);
      margin-bottom: var(--spacing-24);
      word-break: break-word;

      p {
        margin: 0;
      }
    }

    .course-banner {
      background-color: var(--main-dark-shade-1);
      text-align: center;
      color: var(--main-light);
      border-radius: 7px;
      padding: var(--spacing-48) var(--spacing-40);
      height: 100%;

      &.light {
        background-color: var(--main-light-shade-3);
        color: var(--main-dark-shade-0);
      }

      .h3 {
        margin-top: 0;
        margin-bottom: var(--spacing-48);
      }

      a {
        padding: var(--spacing-16) var(--spacing-22) var(--spacing-14) var(--spacing-22);
        font-size: 18px;
        line-height: 18px;
        font-weight: 600;
        color: var(--main-light);
      }
    }

    .banner-holder {
      height: 100%;
      min-height: 284px;
      margin: 0;

      .banner {
        height: 100%;
        justify-content: center;
        padding: var(--spacing-22);
      }
    }
  }

  @include bp($bp-laptop) {
    &.site {
      .hero-section {
        .holder-title {
          .h3 {
            font-size: 32px;
          }
        }
      }
    }

    .hero-section .holder-title {
      .h3 {
        font-size: 32px;
        line-height: 40px;
      }
    }

    .course-library {
      .module-title {
        margin-bottom: var(--spacing-12);
      }

      .module-section {
        padding: 0 0 var(--spacing-50);
        margin: 0 0 var(--spacing-50);

        &.sliding {
          padding: 0 0 var(--spacing-20);
        }
      }
    }
  }

  @include bp($bp-mobile) {
    margin-top: 0 !important;
    padding-top: 0 !important;

    &.site {
      .hero-section {
        min-height: 375px;
        padding-top: 68px;

        .holder-title {
          .h3 {
            font-size: 32px;
          }
        }
      }

      .course-library {
        margin-top: var(--spacing-100);
      }
    }

    .hero-section {
      height: 243px;

      .holder-title {
        .h3 {
          font-size: 32px;
        }
      }
    }

    aside {
      display: none;
    }

    .filters {
      form {
        .search-bar .search-icon {
          top: 14px;
        }
      }
    }

    .course-library {
      padding: 0;
      overflow: hidden;
      margin-top: 0;

      &.results {
        padding-bottom: var(--spacing-24);
      }

      .mobile-section-title {
        display: block;
        padding-left: var(--spacing-24);
        margin-top: 58px;

        &.site {
          text-align: center;
          font-size: 28px;
          margin-bottom: var(--spacing-36);
        }

        &.results {
          margin-top: var(--spacing-40);
        }
      }

      .results {
        padding-left: var(--spacing-24);
      }

      .module-section {
        border-bottom: none;
        padding: 0;
        margin: 0;

        .module-title {
          background-color: var(--main-light-shade-2);
          padding: var(--spacing-12) var(--spacing-24);
          font-size: 16px;
          font-weight: normal;
          margin-bottom: 2px;
          line-height: 26px;
          display: flex;
          align-items: center;
          justify-content: space-between;

          .module-description {
            margin-left: 15px;
            margin-right: 10px;
            word-break: break-word;
          }

          .courses-count {
            display: block;
            font-size: 14px;
            color: var(--main-dark-light-4);
            float: none;
            font-weight: 500;
            line-height: 23px;
          }

          &.expanded {
            svg {
              transform: rotate(180deg);
            }

            .arrow-site {
              transform: rotate(90deg);
            }
          }

          svg {
            display: block;

            path {
              fill: var(--main-light-shade-4);
            }
          }

          .arrow-site {
            width: 15px;
            height: 18px;

            path {
              fill: var(--main-dark);
            }
          }
        }

        .module-title + .row,
        .module-title + div .row {
          height: 0;
          overflow: hidden;
          transition: height 500ms ease-in;
          background-color: var(--main-light-shade-1);

          &.expanded {
            display: flex;
            padding: var(--spacing-16) var(--spacing-24);
          }
        }

        .slider-container {
          .slider-header .heading {
            width: 100%;
            margin-bottom: 0;
          }

          .row {
            height: 0;
            transition: height 500ms ease-in, transform 1s ease-out;
            background-color: var(--main-light-shade-1);
            padding: 0 var(--spacing-24);

            &.expanded {
              display: flex;
              padding-top: var(--spacing-16);
              padding-bottom: var(--spacing-16);
            }

            .course-card {
              margin-bottom: 0;
              height: 100%;
            }
          }
        }

        .module-title.expanded + .row:not(.expanded),
        .module-title.expanded + div .row:not(.expanded) {
          display: flex;
          padding: var(--spacing-16) var(--spacing-24);
        }

        &.sliding {
          border: 0;
          padding: 0 0 var(--spacing-2);
        }
      }
    }

    .search-bar-holder {
      .search-bar {
        input {
          height: 48px;
        }
      }
    }

    .__filters {
      button {
        height: 48px;
        width: 48px;
      }

      .btn-primary {
        height: 40px;
        width: 164px;
      }
    }
  }
}
</style>
